"use client";

type AnalyticsValue = string | number | boolean;

export type ProductAnalyticsEvent =
  | "template_view"
  | "template_use"
  | "registration_start"
  | "registration_complete"
  | "ai_autofill_start"
  | "ai_autofill_complete"
  | "google_calendar_sync_start"
  | "google_calendar_sync_complete"
  | "schedule_export";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Push a GA4 event onto the global dataLayer through a gtag-compatible shim.
 *
 * Why not @next/third-parties' sendGAEvent: it only works while its
 * <GoogleAnalytics> component is mounted, and gtag is now loaded lazily (see
 * app/(site)/[locale]/layout.tsx) so its ~189 KiB stays off the critical path.
 *
 * The pushed value MUST be the `arguments` object, not a plain array: gtag.js
 * silently ignores array-shaped commands, which is how early versions of this
 * helper dropped every event (measured 2026-09-25).
 *
 * Scope: the gtag bootstrap and loader live in
 * app/(site)/[locale]/layout.tsx, so this helper only works inside the
 * app/(site) route group. Calling it from another route group silently drops
 * the event (no config, no gtag.js).
 *
 * Send product funnel events with only aggregate, non-identifying context.
 * Do not add emails, schedule text, calendar data, or user-entered values here.
 */
export function trackProductEvent(
  name: ProductAnalyticsEvent,
  locale: string,
  parameters: Record<string, AnalyticsValue> = {},
): void {
  if (typeof window === "undefined") return;

  window.dataLayer = window.dataLayer ?? [];
  window.gtag ??= function () {
    // gtag.js only consumes an `[object Arguments]` entry; a rest-param array
    // is silently ignored, which is the bug this shim exists to avoid.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments as unknown as unknown[]);
  };
  window.gtag("event", name, {
    locale: locale === "es" ? "es" : "en",
    page: window.location.pathname.slice(0, 200),
    ...parameters,
  });
}

export function getClientLocale(): "en" | "es" {
  return document.documentElement.lang === "es" ? "es" : "en";
}
